import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, Row, Col } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { RejaPendientes, armaBloque, cambiaCeldaBloques, llenaBloques, lineasCapturadas } from "./RejaPendientes";
import { hoyLocal } from "./fechaLocal";

// Surte el pedido desde su propio listado: trae lo pendiente por color y talla
// con la existencia de cada variante, se captura lo que sale y en una sola
// peticion se crea la nota, se descuenta el inventario, se baja lo pendiente
// del renglon y se carga el saldo al cliente.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);


function SurtirPedidoModal({ abierto, cerrar, pedido, user, onSurtido }) {
  const [bloques, setBloques] = useState([]);
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState("");
  const [fecha, setFecha] = useState(hoyLocal());
  const [observaciones, setObservaciones] = useState("NA");
  const [impuestos, setImpuestos] = useState(0);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    axios.get(process.env.REACT_APP_URL_AREAS, { headers: headers() })
      .then((r) => {
        const activas = (r.data || []).filter((a) => a.is_active === "Si" && !/general/i.test(a.name));
        setAreas(activas);
        setArea((prev) => prev || (activas.find((a) => /cedis/i.test(a.name)) || activas[0] || {})._id || "");
      })
      .catch(() => {});
  }, [abierto]);

  // la reja se vuelve a armar cada que cambia el almacen: la existencia es por almacen
  useEffect(() => {
    if (!abierto || !pedido || !area) return;
    setBloques([]);
    axios.get(`${process.env.REACT_APP_URL_ARTICULOS_PEDIDO}/surtirVariantes/${pedido._id}/${area}`, { headers: headers() })
      .then((r) => {
        const lineas = (r.data || []).map((a) => ({
          _id: a.idArtPed,
          articulo: a.articulo,
          color: a.color,
          talla: a.talla,
          pendiente: Number(a.pendSurtir) || 0,
          inv: Number(a.cantInv) || 0,
          precio: Number(a.precio) || 0,
          idInv: a.idInv,
          costoUnitario: Number(a.costoUnitario) || 0,
        })).filter((l) => l.articulo && l.pendiente > 0);
        setBloques(lineas.length
          ? [armaBloque(String(pedido._id), `Pedido ${pedido.numero || pedido.idPedido}`, pedido.clientes || "", lineas)]
          : []);
      })
      .catch((e) => Swal.fire({ icon: "error", title: "No se pudo traer el pedido", text: e.message }));
  }, [abierto, pedido, area]); // eslint-disable-line

  const capturadas = lineasCapturadas(bloques);
  const piezas = capturadas.reduce((s, c) => s + c.cantidad, 0);
  const importe = capturadas.reduce((s, c) => s + c.cantidad * (c.linea.precio || 0), 0);
  const sinInventario = capturadas.filter((c) => !c.linea.idInv);

  async function surtir() {
    if (!capturadas.length) { Swal.fire({ icon: "warning", title: "Sin cantidades", text: "Captura lo que sale." }); return; }
    if (sinInventario.length) {
      Swal.fire({ icon: "error", title: "Sin existencia", text: `${sinInventario.length} renglón(es) no tienen inventario en ese almacén.` });
      return;
    }
    const ok = await Swal.fire({
      icon: "question", title: `Surtir ${num(piezas)} piezas`,
      html: `${capturadas.length} renglón(es) · importe $${num(importe)}<br>Se descuenta del inventario y se le carga al cliente.`,
      showCancelButton: true, confirmButtonText: "Sí, surtir", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_URL_SURTIDOS}DesdePedido`, {
        pedidos: pedido._id, clientes: pedido.idClientes, areas: area, fecha,
        observaciones, impuestos: Number(impuestos) || 0, descuento: 0, flete: 0, comisionTDC: 0,
        colaboradores: pedido.idColaboradores, empresas: pedido.idEmpresas, user,
        lineas: capturadas.map((c) => ({
          articulosPedido: c.linea._id, inventarios: c.linea.idInv,
          articulos: c.linea.articulo?._id,
          colores: c.linea.color?._id || null, tallas: c.linea.talla?._id || null,
          cantidad: c.cantidad, precio: c.linea.precio, costoUnitario: c.linea.costoUnitario,
        })),
      }, { headers: headers() });
      setGuardando(false);
      await Swal.fire({
        icon: "success", title: `Nota ${r.data.idSurtido} creada`,
        html: `${num(r.data.total_cantidad)} piezas · $${num(r.data.total_general)}` +
              (r.data.pedidoCerrado ? "<br>El pedido quedó <b>cerrado</b>." : "<br>El pedido sigue abierto."),
      });
      cerrar();
      if (onSurtido) onSurtido();
    } catch (error) {
      setGuardando(false);
      Swal.fire({ icon: "error", title: "No se pudo surtir", text: error.response?.data?.message || error.message });
    }
  }

  return (
    <Modal size="xl" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>
        Surtir pedido {pedido?.numero || pedido?.idPedido} {pedido?.clientes ? `· ${pedido.clientes}` : ""}
      </ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col md={2}>
            <Label>Fecha</Label>
            <Input bsSize="sm" type="date" value={fecha} onChange={(e) => { const v = e.target.value; setFecha(v); }} />
          </Col>
          <Col md={3}>
            <Label>Almacén que surte</Label>
            <Input bsSize="sm" type="select" value={area} onChange={(e) => { const v = e.target.value; setArea(v); }}>
              <option value="">Selecciona</option>
              {areas.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
            </Input>
          </Col>
          <Col md={2}>
            <Label>Impuestos %</Label>
            <Input bsSize="sm" type="number" step="any" value={impuestos} onChange={(e) => { const v = e.target.value; setImpuestos(v); }} />
          </Col>
          <Col md={5}>
            <Label>Observaciones</Label>
            <Input bsSize="sm" value={observaciones} onChange={(e) => { const v = e.target.value; setObservaciones(v); }} />
          </Col>
        </Row>
        <RejaPendientes
          bloques={bloques}
          etiquetaPendiente="Falta"
          onCambia={(b, a, c, t, v) => setBloques((prev) => cambiaCeldaBloques(prev, b, a, c, t, v))}
          onLlenar={(b, a, c) => setBloques((prev) => llenaBloques(prev, b, a, true, c))}
          onLimpiar={(b, a, c) => setBloques((prev) => llenaBloques(prev, b, a, false, c))}
          onQuitar={() => {}}
        />
      </ModalBody>
      <ModalFooter>
        <Label style={{ marginRight: "auto", color: "#64748b", fontSize: 12 }}>
          {capturadas.length} renglón(es) · <b>{num(piezas)}</b> piezas · ${num(importe)}
        </Label>
        <Button color="success" onClick={surtir} disabled={guardando || !piezas}>
          {guardando ? "Surtiendo..." : "Surtir"}
        </Button>
        <Button color="secondary" onClick={cerrar}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default SurtirPedidoModal;
